import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useEmbeddedNavigation } from '../ptcm/hooks/useEmbeddedMode.js';
import { useDeleteAccount } from '../ptcm/context/DeleteAccountContext.jsx';

const UserMenu = ({
  currentUser,
  logout,
  darkMode,
  toggleDarkMode,
  needsRecoveryEmail = false,
  recoveryEmailStatus = 'missing',
  onOpenRecoveryEmailPrompt = null
}) => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const navigate = useNavigate();
  const { toEmbeddedPath } = useEmbeddedNavigation();
  const { open: openDeleteAccount } = useDeleteAccount();

  const handleLogout = () => {
    if (logout) {
      logout();
    }
    setIsMenuOpen(false);
  };

  return (
    <div className="relative">
      <button
        onClick={() => setIsMenuOpen(!isMenuOpen)}
        className={`flex items-center gap-2 px-3.5 py-2 rounded-full border border-ptcm-border dark:border-gray-700 bg-white/90 dark:bg-gray-800 hover:bg-white dark:hover:bg-gray-700 transition-all shadow-ptcm-float ${isMenuOpen ? 'ring-2 ring-ptcm-primary/15 dark:ring-blue-900' : ''}`}
      >
        <div className="relative">
          <div className="w-7 h-7 rounded-full bg-ptcm-primary-soft dark:from-blue-900 dark:to-indigo-900 flex items-center justify-center text-xs font-semibold text-ptcm-primary dark:text-blue-300">
            {currentUser?.name?.[0] || currentUser?.email?.[0] || 'U'}
          </div>
          {needsRecoveryEmail ? (
            <span className={`absolute -right-0.5 -top-0.5 h-3 w-3 rounded-full border-2 border-white ${recoveryEmailStatus === 'pending' ? 'bg-amber-500' : 'bg-red-500'} dark:border-gray-800`} />
          ) : null}
        </div>
        <span className="text-sm font-medium text-ptcm-ink dark:text-gray-200 hidden sm:block">
          {currentUser?.name || currentUser?.email || 'Paramètres'}
        </span>
        <svg xmlns="http://www.w3.org/2000/svg" className={`h-4 w-4 text-ptcm-muted transition-transform duration-200 ${isMenuOpen ? 'rotate-180' : ''}`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </button>

      {isMenuOpen && (
        <>
          <div
            className="fixed inset-0 z-10"
            onClick={() => setIsMenuOpen(false)}
          />
          <div className={`absolute right-0 mt-3 ${needsRecoveryEmail ? 'w-72 max-w-[calc(100vw-1.5rem)]' : 'w-56'} rounded-[28px] shadow-ptcm-soft dark:shadow-black/50 bg-white dark:bg-gray-900 border border-ptcm-border/80 dark:ring-white/10 z-50 transform origin-top-right transition-all`}>
            <div className="p-2 space-y-1">
              {needsRecoveryEmail && onOpenRecoveryEmailPrompt ? (
                <button
                  onClick={() => {
                    setIsMenuOpen(false);
                    onOpenRecoveryEmailPrompt();
                  }}
                  className={`w-full rounded-2xl px-3 py-3 text-left transition-colors ${recoveryEmailStatus === 'pending'
                    ? 'border border-amber-200/80 bg-amber-50/85 hover:bg-amber-100/80 dark:border-amber-900/40 dark:bg-amber-900/20 dark:hover:bg-amber-900/30'
                    : 'border border-red-200/80 bg-red-50/85 hover:bg-red-100/80 dark:border-red-900/40 dark:bg-red-900/20 dark:hover:bg-red-900/30'}`}
                >
                  <div className="flex items-start gap-3">
                    <div className={`mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-white shadow-sm dark:bg-gray-900 ${recoveryEmailStatus === 'pending' ? 'text-amber-700 dark:text-amber-300' : 'text-red-600 dark:text-red-300'}`}>
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 12H8m0 0l3-3m-3 3l3 3M4 6a2 2 0 012-2h8a2 2 0 012 2v2m4 4v6a2 2 0 01-2 2H6a2 2 0 01-2-2v-6a2 2 0 012-2h12a2 2 0 012 2z" />
                      </svg>
                    </div>
                    <div>
                      <div className={`text-sm font-semibold ${recoveryEmailStatus === 'pending' ? 'text-amber-800 dark:text-amber-200' : 'text-red-700 dark:text-red-200'}`}>
                        {recoveryEmailStatus === 'pending' ? 'Verifier mon email' : 'Enregistrer mon compte'}
                      </div>
                      <div className={`mt-0.5 text-xs ${recoveryEmailStatus === 'pending' ? 'text-amber-700/90 dark:text-amber-200/80' : 'text-red-600/90 dark:text-red-200/80'}`}>
                        {recoveryEmailStatus === 'pending'
                          ? 'Renvoyez un lien de verification pour activer la reconnexion email.'
                          : 'Ajoutez un email pour retrouver votre acces plus facilement.'}
                      </div>
                    </div>
                  </div>
                </button>
              ) : null}

              <div className="px-3 py-2 flex items-center justify-between bg-ptcm-panel/70 dark:bg-gray-800/50 rounded-2xl mb-2">
                <span className="text-sm text-ptcm-ink dark:text-gray-200">Thème</span>
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    toggleDarkMode();
                  }}
                  className="p-1 rounded-full hover:bg-white dark:hover:bg-gray-700 transition-colors"
                  aria-label="Basculer le mode sombre"
                >
                  {darkMode ? (
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-yellow-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 3v1m0 16v1m9-9h-1M4 12H3m15.364 6.364l-.707-.707M6.343 6.343l-.707-.707m12.728 0l-.707.707M6.343 17.657l-.707.707M16 12a4 4 0 11-8 0 4 4 0 018 0z" />
                    </svg>
                  ) : (
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-ptcm-ink" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20.354 15.354A9 9 0 018.646 3.646 9.003 9.003 0 0012 21a9.003 9.003 0 008.354-5.646z" />
                    </svg>
                  )}
                </button>
              </div>

              <div className="border-t border-ptcm-border dark:border-gray-700"></div>

              <button
                onClick={() => {
                  navigate(toEmbeddedPath('/'));
                  setIsMenuOpen(false);
                }}
                className="flex w-full items-center gap-2 px-3 py-2.5 text-sm font-medium text-ptcm-ink dark:text-gray-200 rounded-2xl hover:bg-ptcm-panel dark:hover:bg-gray-800 transition-colors"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" /></svg>
                Accueil
              </button>

              <button
                onClick={() => {
                  navigate(toEmbeddedPath('/assistance'));
                  setIsMenuOpen(false);
                }}
                className="flex w-full items-center gap-2 px-3 py-2.5 text-sm font-medium text-ptcm-ink dark:text-gray-200 rounded-2xl hover:bg-ptcm-panel dark:hover:bg-gray-800 transition-colors"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.228 9c.549-1.165 1.918-2 3.272-2 1.934 0 3.5 1.343 3.5 3 0 1.292-.953 2.393-2.286 2.816-.567.18-.964.67-.964 1.181V15M12 18h.01M22 12c0 5.523-4.477 10-10 10S2 17.523 2 12 6.477 2 12 2s10 4.477 10 10z" /></svg>
                Assistance
              </button>

              <button
                onClick={() => {
                  navigate(toEmbeddedPath('/confidentialite'));
                  setIsMenuOpen(false);
                }}
                className="flex w-full items-center gap-2 px-3 py-2.5 text-sm font-medium text-ptcm-ink dark:text-gray-200 rounded-2xl hover:bg-ptcm-panel dark:hover:bg-gray-800 transition-colors"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 3l7 4v5c0 4.97-3.058 8.478-7 9-3.942-.522-7-4.03-7-9V7l7-4z" /></svg>
                Confidentialite
              </button>

              {currentUser && logout && (
                <button
                  onClick={handleLogout}
                  className="flex w-full items-center gap-2 px-3 py-2.5 text-sm font-medium text-red-600 dark:text-red-400 rounded-2xl hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" /></svg>
                  Déconnexion
                </button>
              )}

              {currentUser && (
                <>
                  <div className="border-t border-ptcm-border dark:border-gray-700"></div>
                  <button
                    onClick={() => {
                      setIsMenuOpen(false);
                      openDeleteAccount();
                    }}
                    className="flex w-full items-center gap-2 px-3 py-2.5 text-sm font-medium text-red-600 dark:text-red-400 rounded-2xl hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" /></svg>
                    Supprimer mon compte
                  </button>
                </>
              )}
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default UserMenu;